import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { FileText, Scale, Shield, CreditCard, Users, Mail } from "lucide-react";

const LegalPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to hash if present
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const sections = [
    { id: "mentions", label: "Mentions Légales", icon: FileText },
    { id: "cgv", label: "CGV", icon: CreditCard },
    { id: "confidentialite", label: "Confidentialité", icon: Shield },
    { id: "droits", label: "Droits d'Auteur", icon: Scale },
  ];

  return (
    <div className="min-h-screen py-12 px-6 md:px-12" data-testid="legal-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <span className="font-heading text-sm tracking-widest text-[#FFB347] mb-2 block">
            INFORMATIONS LÉGALES
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Mentions Légales & CGV
          </h1>
          <p className="text-[#A8A29E] text-lg">
            Toutes les informations légales concernant Sapito-Records et l'utilisation de ce site.
          </p>
        </motion.div>

        {/* Quick Nav */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-3 mb-12"
        >
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="flex items-center gap-2 px-4 py-2 bg-[#141210] rounded-full border border-[rgba(139,69,19,0.2)] text-[#A8A29E] hover:text-[#FFB347] hover:border-[#FFB347] transition-all"
              data-testid={`nav-${section.id}`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </a>
          ))}
        </motion.div>

        {/* Content */}
        <div className="space-y-12">
          {/* Mentions Légales */}
          <motion.section
            id="mentions"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.2)] p-8"
            data-testid="section-mentions"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#FFB347]/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#FFB347]" />
              </div>
              <h2 className="font-display text-2xl font-bold">Mentions Légales</h2>
            </div>

            <div className="prose prose-invert max-w-none space-y-6 text-[#A8A29E]">
              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Éditeur du site</h3>
                <p>
                  <strong className="text-[#F5F5F0]">Sapito-Records</strong><br />
                  Label de production discographique indépendant<br />
                  France
                </p>
                <p className="mt-2">
                  Email : contact@Sapito-Records.com<br />
                  Site web : www.Sapito-Records.com
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Directeur de la publication</h3>
                <p>Le gérant de Sapito-Records</p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Hébergement</h3>
                <p>
                  Ce site est hébergé par des services d'hébergement professionnels conformes au RGPD.
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Numéro d'identification</h3>
                <p>
                  Sapito-Records est un label de production discographique enregistré en France.<br />
                  Les informations d'immatriculation complètes sont disponibles sur demande.
                </p>
              </div>
            </div>
          </motion.section>

          {/* CGV */}
          <motion.section
            id="cgv"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.2)] p-8"
            data-testid="section-cgv"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#FFB347]/20 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#FFB347]" />
              </div>
              <h2 className="font-display text-2xl font-bold">Conditions Générales de Vente</h2>
            </div>

            <div className="prose prose-invert max-w-none space-y-6 text-[#A8A29E]">
              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Article 1 - Objet</h3>
                <p>
                  Les présentes conditions générales régissent les relations entre Sapito-Records et ses partenaires, 
                  artistes et utilisateurs dans le cadre de la production, la distribution et la diffusion de contenus musicaux.
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Article 2 - Distribution musicale</h3>
                <p>
                  Sapito-Records assure la distribution de ses productions via les plateformes de streaming légales 
                  (Spotify, Apple Music, Deezer, etc.) et son propre canal SoundCloud. Toute distribution est effectuée 
                  dans le respect des droits d'auteur et des réglementations en vigueur.
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Article 3 - Propriété intellectuelle</h3>
                <p>
                  Toutes les œuvres musicales diffusées sur ce site et les plateformes associées sont protégées par le droit d'auteur. 
                  Leur reproduction, distribution ou modification sans autorisation écrite préalable est strictement interdite.
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Article 4 - Soumission de démos</h3>
                <p>
                  L'envoi d'une démo à Sapito-Records ne constitue pas un engagement de la part du label. 
                  Les artistes conservent l'intégralité de leurs droits sur leurs œuvres jusqu'à la signature 
                  éventuelle d'un contrat de licence ou de distribution.
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Article 5 - Responsabilité</h3>
                <p>
                  Sapito-Records s'engage à respecter toutes les obligations légales liées à son activité de label discographique, 
                  notamment en matière de déclaration auprès des organismes de gestion collective (SACEM, SDRM, SCPP).
                </p>
              </div>
            </div>
          </motion.section>

          {/* Confidentialité */}
          <motion.section
            id="confidentialite"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.2)] p-8"
            data-testid="section-confidentialite"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#FFB347]/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#FFB347]" />
              </div>
              <h2 className="font-display text-2xl font-bold">Politique de Confidentialité</h2>
            </div>

            <div className="prose prose-invert max-w-none space-y-6 text-[#A8A29E]">
              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Collecte des données</h3>
                <p>
                  Sapito-Records collecte uniquement les données nécessaires au bon fonctionnement de ses services :
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Données de contact (nom, email) via le formulaire de contact</li>
                  <li>Adresse email pour l'inscription à la newsletter</li>
                  <li>Liens SoundCloud pour les soumissions de démos</li>
                </ul>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Utilisation des données</h3>
                <p>
                  Les données collectées sont utilisées exclusivement pour :
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Répondre aux demandes de contact</li>
                  <li>Évaluer les démos soumises</li>
                  <li>Envoyer la newsletter (sur consentement)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Conservation des données</h3>
                <p>
                  Conformément au RGPD, vos données sont conservées pour une durée limitée et proportionnée à leur finalité. 
                  Vous pouvez à tout moment demander leur suppression en nous contactant.
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Vos droits</h3>
                <p>
                  Conformément à la loi Informatique et Libertés et au RGPD, vous disposez des droits suivants :
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Droit d'accès à vos données</li>
                  <li>Droit de rectification</li>
                  <li>Droit à l'effacement</li>
                  <li>Droit à la portabilité</li>
                  <li>Droit d'opposition</li>
                </ul>
                <p className="mt-2">
                  Pour exercer ces droits, contactez-nous à : contact@Sapito-Records.com
                </p>
              </div>
            </div>
          </motion.section>

          {/* Droits d'auteur */}
          <motion.section
            id="droits"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.2)] p-8"
            data-testid="section-droits"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#FFB347]/20 flex items-center justify-center">
                <Scale className="w-5 h-5 text-[#FFB347]" />
              </div>
              <h2 className="font-display text-2xl font-bold">Droits d'Auteur & SACEM</h2>
            </div>

            <div className="prose prose-invert max-w-none space-y-6 text-[#A8A29E]">
              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Protection des œuvres</h3>
                <p>
                  Toutes les productions musicales du catalogue Sapito-Records sont protégées par le droit d'auteur français 
                  et les conventions internationales. Les œuvres sont déposées et leurs droits sont dûment enregistrés.
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">SACEM & SDRM</h3>
                <p>
                  Sapito-Records travaille en conformité avec les organismes de gestion collective des droits d'auteur :
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li><strong className="text-[#F5F5F0]">SACEM</strong> - Société des Auteurs, Compositeurs et Éditeurs de Musique</li>
                  <li><strong className="text-[#F5F5F0]">SDRM</strong> - Société pour l'administration du Droit de Reproduction Mécanique</li>
                  <li><strong className="text-[#F5F5F0]">SCPP</strong> - Société Civile des Producteurs Phonographiques</li>
                </ul>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Licence d'utilisation</h3>
                <p>
                  L'écoute des productions sur ce site et sur SoundCloud est autorisée dans un cadre personnel et non commercial. 
                  Toute utilisation commerciale, synchronisation, ou diffusion publique nécessite une autorisation préalable 
                  et le paiement des redevances appropriées.
                </p>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold text-[#F5F5F0] mb-2">Demandes de licence</h3>
                <p>
                  Pour toute demande de licence, synchronisation, ou utilisation commerciale de nos productions, 
                  veuillez nous contacter via notre formulaire de contact en précisant :
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Le(s) titre(s) concerné(s)</li>
                  <li>L'usage prévu (publicité, film, événement, etc.)</li>
                  <li>La durée et le territoire d'exploitation</li>
                </ul>
              </div>

              <div className="p-4 bg-[#FFB347]/10 rounded-lg border border-[#FFB347]/30">
                <p className="text-[#FFB347] font-medium">
                  ⚠️ Toute utilisation non autorisée des œuvres musicales de Sapito-Records est passible de poursuites 
                  conformément au Code de la Propriété Intellectuelle.
                </p>
              </div>
            </div>
          </motion.section>
        </div>

        {/* Contact for questions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 p-6 bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] flex items-center justify-between flex-wrap gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FFB347]/20 flex items-center justify-center">
              <Mail className="w-6 h-6 text-[#FFB347]" />
            </div>
            <div>
              <h3 className="font-display font-semibold">Des questions ?</h3>
              <p className="text-[#A8A29E] text-sm">Contactez-nous pour toute question juridique</p>
            </div>
          </div>
          <a href="/contact" className="btn-primary" data-testid="contact-legal-btn">
            Nous Contacter
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default LegalPage;
